import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ExpandMore, ExpandLess, FileCopyOutlined, Visibility, VisibilityOff, Undo } from "@material-ui/icons";
import ChannelIcon from "../../ChannelIcon";
import toastError from "../../../errors/toastError";
import { getPlatformIntegrations, revealPlatformSecret, savePlatformIntegration } from "../../../services/platformIntegrations.api";
import { FIELD_LABELS, PROVIDER_INFO, SOURCE_LABEL, STATUS_INFO, describeError } from "./catalog";

/**
 * Configuración → Integraciones (doc 0440), solo superadmin. Las credenciales de PLATAFORMA que antes
 * había que escribir en el `.env` del servidor: se guardan cifradas y se comprueban contra Meta o
 * Telegram antes de aceptarlas. Lo que ya estaba en el `.env` sigue valiendo y se indica de dónde sale.
 */
const useStyles = makeStyles((theme) => ({
  card: { padding: theme.spacing(2, 2.5), marginBottom: theme.spacing(2), borderRadius: 12 },
  head: { display: "flex", alignItems: "center", gap: 12 },
  title: { fontWeight: 600, fontSize: "1rem", flex: 1 },
  what: { margin: theme.spacing(1, 0, 0.5), fontSize: 13, color: theme.palette.text.secondary, lineHeight: 1.45 },
  where: { fontSize: 12 },
  section: {
    margin: theme.spacing(2, 0, 1),
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary
  },
  field: { marginBottom: theme.spacing(1.5), "& .MuiOutlinedInput-root": { fontSize: "0.85rem" } },
  source: { fontSize: 11, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 4, marginTop: 2 },
  url: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "monospace",
    fontSize: 12,
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: "4px 8px",
    marginBottom: 6,
    wordBreak: "break-all"
  },
  actions: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: theme.spacing(1) },
  meta: { fontSize: 11, color: theme.palette.text.secondary, marginRight: "auto" }
}));

const copiar = (texto) => {
  navigator.clipboard?.writeText(texto).then(() => toast.success("Copiado"), () => toast.error("No se pudo copiar"));
};

const ProviderCard = ({ view, urls, onSaved, abiertoInicial }) => {
  const classes = useStyles();
  const info = PROVIDER_INFO[view.provider];
  const etiquetas = FIELD_LABELS[view.provider] || {};
  const estado = STATUS_INFO[view.status] || STATUS_INFO.empty;
  const [cambios, setCambios] = useState({});
  const [visibles, setVisibles] = useState({});
  const [avanzado, setAvanzado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setCambios({});
    setVisibles({});
  }, [view]);

  const basicos = view.fields.filter((f) => !f.advanced);
  const avanzados = view.fields.filter((f) => f.advanced);
  const hayCambios = Object.keys(cambios).length > 0;

  const valorDe = (f) => {
    if (Object.prototype.hasOwnProperty.call(cambios, f.key)) return cambios[f.key];
    if (f.kind === "secret") return visibles[f.key] ?? "";
    return f.value ?? "";
  };

  const cambiar = (key, valor) => setCambios((c) => ({ ...c, [key]: valor }));

  const mostrar = async (f) => {
    if (visibles[f.key] !== undefined) {
      setVisibles((v) => {
        const { [f.key]: _, ...resto } = v;
        return resto;
      });
      return;
    }
    try {
      const { data } = await revealPlatformSecret(view.provider, f.key);
      setVisibles((v) => ({ ...v, [f.key]: data.value || "" }));
    } catch (err) {
      toastError(err);
    }
  };

  const guardar = async () => {
    try {
      setGuardando(true);
      const { data } = await savePlatformIntegration(view.provider, cambios);
      toast.success(data.verified ? `Guardado y comprobado${data.detail ? `: ${data.detail}` : ""}` : "Guardado");
      onSaved(data.providers);
    } catch (err) {
      toast.error(describeError(err, view.provider), { autoClose: 10000 });
    } finally {
      setGuardando(false);
    }
  };

  const renderCampo = (f) => {
    const [label, ayuda] = etiquetas[f.key] || [f.key, ""];
    const fuente = SOURCE_LABEL[f.source];
    const quitar =
      f.source === "panel" ? (
        <Tooltip title="Quitar del panel: volverá a usarse el archivo de configuración del servidor o el valor por defecto">
          <IconButton size="small" onClick={() => cambiar(f.key, "")} style={{ padding: 2 }}>
            <Undo style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      ) : null;
    const pie = (
      <span className={classes.source}>
        {cambios[f.key] === "" ? "Se quitará al guardar" : fuente}
        {cambios[f.key] === "" ? null : quitar}
      </span>
    );

    if (f.kind === "boolean") {
      const actual = Object.prototype.hasOwnProperty.call(cambios, f.key) ? cambios[f.key] === "true" : f.value === "true";
      return (
        <Box key={f.key} className={classes.field}>
          <Box display="flex" alignItems="center">
            <Switch size="small" color="primary" checked={actual} onChange={(e) => cambiar(f.key, String(e.target.checked))} />
            <Typography variant="body2">{label}</Typography>
          </Box>
          <Typography variant="caption" color="textSecondary">{ayuda}</Typography>
          {pie}
        </Box>
      );
    }

    const secreto = f.kind === "secret";
    const placeholder = secreto && f.preview ? `${f.preview === "configurado" ? "Configurado" : `Configurado (${f.preview})`} — déjalo vacío para conservarlo` : "";
    return (
      <Box key={f.key} className={classes.field}>
        <TextField
          label={`${label}${f.required ? " *" : ""}`}
          helperText={ayuda}
          value={valorDe(f)}
          placeholder={placeholder}
          InputLabelProps={placeholder ? { shrink: true } : undefined}
          onChange={(e) => cambiar(f.key, e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          autoComplete="off"
          type={secreto && visibles[f.key] === undefined && !Object.prototype.hasOwnProperty.call(cambios, f.key) ? "password" : "text"}
          InputProps={
            secreto && f.preview
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={visibles[f.key] !== undefined ? "Ocultar" : "Mostrar (queda anotado en Seguridad)"}>
                        <IconButton size="small" onClick={() => mostrar(f)}>
                          {visibles[f.key] !== undefined ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }
              : undefined
          }
        />
        {pie}
      </Box>
    );
  };

  const urlsDelCanal = [];
  if (view.provider === "meta" && urls?.metaWebhook) urlsDelCanal.push(["URL del webhook (en Meta → Webhooks)", urls.metaWebhook]);
  if (view.provider === "instagram" && urls?.instagramRedirect) urlsDelCanal.push(["URL de retorno (en Meta → Instagram → Configuración del inicio de sesión)", urls.instagramRedirect]);

  return (
    <Paper variant="outlined" className={classes.card} id={`integracion-${view.provider}`} style={abiertoInicial ? { borderColor: "#1976d2" } : undefined}>
      <Box className={classes.head}>
        <ChannelIcon provider={info.icon} size={30} />
        <Typography className={classes.title}>{info.title}</Typography>
        <Chip size="small" label={estado.label} style={{ color: estado.color, background: estado.bg, fontWeight: 600 }} />
      </Box>
      <Typography className={classes.what}>{info.what}</Typography>
      <Typography className={classes.where}>
        Dónde se consigue:{" "}
        <Link href={info.where.url} target="_blank" rel="noopener noreferrer">
          {info.where.label}
        </Link>
      </Typography>

      {urlsDelCanal.length > 0 && (
        <>
          <Typography className={classes.section}>Copia esto en Meta</Typography>
          {urlsDelCanal.map(([titulo, url]) => (
            <Box key={titulo}>
              <Typography variant="caption" color="textSecondary">{titulo}</Typography>
              <Box className={classes.url}>
                <span style={{ flex: 1 }}>{url}</span>
                <IconButton size="small" onClick={() => copiar(url)}>
                  <FileCopyOutlined style={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </>
      )}

      <Typography className={classes.section}>Credenciales</Typography>
      {basicos.map(renderCampo)}

      {avanzados.length > 0 && (
        <>
          <Button size="small" onClick={() => setAvanzado((a) => !a)} endIcon={avanzado ? <ExpandLess /> : <ExpandMore />}>
            Opciones avanzadas
          </Button>
          <Collapse in={avanzado}>
            <Box mt={1.5}>{avanzados.map(renderCampo)}</Box>
          </Collapse>
        </>
      )}

      <Box className={classes.actions}>
        <span className={classes.meta}>
          {view.updatedAt
            ? `Último cambio: ${new Date(view.updatedAt).toLocaleString()}${view.updatedByName ? ` · ${view.updatedByName}` : ""}`
            : null}
        </span>
        {hayCambios && (
          <Button size="small" onClick={() => setCambios({})} disabled={guardando}>
            Descartar
          </Button>
        )}
        <Button size="small" color="primary" variant="contained" onClick={guardar} disabled={!hayCambios || guardando}>
          Guardar y comprobar
          {guardando && <CircularProgress size={14} style={{ marginLeft: 8 }} />}
        </Button>
      </Box>
    </Paper>
  );
};

const PlatformIntegrations = ({ focus }) => {
  const [datos, setDatos] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await getPlatformIntegrations();
      setDatos(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!datos || !focus) return;
    const el = document.getElementById(`integracion-${focus}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [datos, focus]);

  const proveedores = useMemo(() => datos?.providers || [], [datos]);

  if (!datos) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box maxWidth={760}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Credenciales de la plataforma: se configuran <b>una sola vez</b> para todas las empresas. Se guardan cifradas y se comprueban con
        Meta o Telegram antes de aceptarlas. Las empresas nunca las ven: solo ven el canal disponible.
      </Typography>
      {proveedores.map((p) => (
        <ProviderCard
          key={p.provider}
          view={p}
          urls={datos.urls}
          abiertoInicial={focus === p.provider}
          onSaved={(nuevos) => setDatos((d) => ({ ...d, providers: nuevos }))}
        />
      ))}
    </Box>
  );
};

export default PlatformIntegrations;
